import {
	AlertDiamondIcon,
	Tick02Icon,
	Time04Icon,
} from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { Badge } from "@/components/ui/badge";

export interface VerificationEntry {
	verified: boolean;
	verification_expires_at?: string | null;
	verification_expiring_soon?: boolean;
}

interface EntriesProps {
	entries: VerificationEntry[];
	verifiedCount?: never;
	totalCount?: never;
	expiringSoon?: never;
	expired?: never;
}

interface CountsProps {
	entries?: never;
	verifiedCount: number;
	totalCount: number;
	expiringSoon?: boolean;
	expired?: boolean;
}

type VerificationStatusBadgeProps = EntriesProps | CountsProps;

function isExpired(expiresAt: string | null | undefined): boolean {
	if (!expiresAt) return false;
	return new Date(expiresAt) < new Date();
}

function isExpiringSoon(entry: VerificationEntry, daysThreshold = 30): boolean {
	if (entry.verification_expiring_soon !== undefined) {
		return entry.verification_expiring_soon;
	}
	if (!entry.verification_expires_at) return false;
	const expiresAt = new Date(entry.verification_expires_at);
	const threshold = new Date();
	threshold.setDate(threshold.getDate() + daysThreshold);
	return expiresAt <= threshold && expiresAt >= new Date();
}

export function VerificationStatusBadge(props: VerificationStatusBadgeProps) {
	let total: number;
	let verifiedCount: number;
	let hasExpired: boolean;
	let hasExpiringSoon: boolean;

	if ("entries" in props && props.entries) {
		const entries = props.entries;
		if (entries.length === 0) {
			return <span className="text-muted-foreground">—</span>;
		}

		total = entries.length;
		const verifiedEntries = entries.filter((e) => e.verified);
		verifiedCount = verifiedEntries.length;
		hasExpired = verifiedEntries.some((e) =>
			isExpired(e.verification_expires_at),
		);
		hasExpiringSoon = verifiedEntries.some((e) => isExpiringSoon(e));
	} else {
		total = props.totalCount;
		verifiedCount = props.verifiedCount;
		hasExpired = props.expired ?? false;
		hasExpiringSoon = props.expiringSoon ?? false;

		if (total === 0) {
			return <span className="text-muted-foreground">—</span>;
		}
	}

	// None verified
	if (verifiedCount === 0) {
		return <Badge variant="secondary">Neověřeno</Badge>;
	}

	// Partially verified
	if (verifiedCount < total) {
		return (
			<Badge variant="outline">
				{verifiedCount}/{total} ověřeno
			</Badge>
		);
	}

	// All verified - check expiration status
	if (hasExpired) {
		return (
			<Badge variant="destructive" className="gap-1">
				<HugeiconsIcon
					icon={AlertDiamondIcon}
					strokeWidth={2}
					className="size-3"
				/>
				Expirováno
			</Badge>
		);
	}

	if (hasExpiringSoon) {
		return (
			<Badge variant="warning" className="gap-1">
				<HugeiconsIcon icon={Time04Icon} strokeWidth={2} className="size-3" />
				Expiruje brzy
			</Badge>
		);
	}

	return (
		<Badge variant="default" className="gap-1">
			<HugeiconsIcon icon={Tick02Icon} strokeWidth={2} className="size-3" />
			Ověřeno
		</Badge>
	);
}
